import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  dogrulamaBekleyenler,
  dogrulanmamisParametreler,
  kilitHazirligi,
  konuAgaci,
  rehberAra,
  rehberDisaAktar,
  rehberTumu,
  type DisaAktarimTuru,
  type DogrulamaBekleyen,
  type DogrulanmamisParametre,
  type KararTipi,
  type KilitHazirligi,
  type KonuDali,
  type RehberBolumu,
  type RehberKonu,
  type RehberRol,
} from '../api/rehber';
import { Tablo, type Sutun } from '../bilesenler/Tablo';
import { Dugme, HataKutusu, Sekme } from '../bilesenler/temel';
import { dosyaAdiParcasi, metinDosyasiIndir } from '../bicim/indir';
import { tarihMetni } from '../bicim/tarih';
import { geciktirmeKullan } from '../kancalar/geciktirme';
import { yetkiKullan, yetkilerHazirKullan } from '../yetki/yetki';

/**
 * MEVZUAT VE REHBER.
 *
 * OKUMA UÇLARI YETKİ İSTEMEZ — bu ekran her kullanıcıya açıktır. Sistem
 * personelin ücretini hesaplıyor; "bu tutar hangi kurala göre çıktı"
 * sorusuna cevap bulabilmek itiraz hakkının ön koşulu.
 *
 * DENETİM SEKMESİ AYRI ve İÇİNDE İKİ FARKLI YETKİ VAR:
 *   iki liste  → SISTEM_YONET | DONEM_KAPAT | RAPOR_GORUNTULE
 *   kilit özeti → SISTEM_YONET | DONEM_KAPAT          (RAPOR_GORUNTULE YOK)
 *
 * Tek değişkenle yazılsaydı `RAPOR_GORUNTULE` sahibi İK kullanıcısı listeleri
 * görür ama özet kutusunda 403 alırdı — SÖZLEŞME §11'in 0.29.0'da ikinci kez
 * kırılan deseninin aynısı.
 */

const EN_AZ_ARAMA = 2;

const TIP_ETIKETI: Record<KararTipi, string> = {
  yasal: 'Yasal',
  ictihat: 'İçtihat',
  tercih: 'Kurum tercihi',
  operasyonel: 'Operasyonel',
  kapsam_disi: 'Kapsam dışı',
};

const TIP_BICIMI: Record<KararTipi, string> = {
  yasal: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  ictihat: 'bg-sky-50 text-sky-700 ring-sky-200',
  tercih: 'bg-amber-50 text-amber-700 ring-amber-200',
  operasyonel: 'bg-slate-100 text-slate-700 ring-slate-200',
  kapsam_disi: 'bg-slate-50 text-slate-500 ring-slate-200',
};

const ROL_ETIKETI: Record<RehberRol, string> = {
  personel: 'Personel',
  puantor: 'Puantör',
  sef: 'Şef',
  mudur: 'Müdür',
  ik: 'İnsan Kaynakları',
  yonetim: 'Sistem Yönetimi',
  denetim: 'Denetim',
};

type SekmeKodu = 'konular' | 'ara' | 'denetim';

export function Rehber() {
  const yetkilerHazir = yetkilerHazirKullan();
  const denetimListesiGorebilir = yetkiKullan('SISTEM_YONET', 'DONEM_KAPAT', 'RAPOR_GORUNTULE');
  const kilitOzetiGorebilir = yetkiKullan('SISTEM_YONET', 'DONEM_KAPAT');

  // Sekme TÜRETİLİR (SÖZLEŞME §5): denetim sekmesi yetkiye bağlı ve yetkiler
  // asenkron geliyor. `useState` ile dondurulsaydı doğrudan F5 ile giren
  // yetkili kullanıcı için ilk seçim yanlış olurdu.
  const [sekmeSecimi, sekmeSecimiYaz] = useState<SekmeKodu | null>(null);
  const sekme: SekmeKodu = sekmeSecimi ?? 'konular';

  const [rol, rolYaz] = useState<RehberRol | ''>('');
  const secilenRol = rol === '' ? undefined : rol;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Mevzuat ve Rehber</h1>
          <p className="mt-1 text-sm text-slate-600">
            Her madde dört şeyi birlikte söyler: mevzuatın ne dediği, sistemin ne yaptığı, rakamlı
            örnek ve mevzuat sessizse hangi yorumun neden seçildiği.
          </p>
        </div>

        <DisaAktarim rol={secilenRol} />
      </header>

      <div className="flex flex-wrap items-center gap-4">
        <label className="text-sm text-slate-700">
          <span className="mr-2">Rol</span>
          <select
            value={rol}
            onChange={(olay) => rolYaz(olay.target.value as RehberRol | '')}
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
          >
            <option value="">Tüm roller</option>
            {(Object.keys(ROL_ETIKETI) as RehberRol[]).map((r) => (
              <option key={r} value={r}>
                {ROL_ETIKETI[r]}
              </option>
            ))}
          </select>
        </label>
        <p className="text-xs text-slate-500">
          77 maddenin hepsini kimse okumaz; rol süzgeci yalnız size hitap edenleri bırakır.
        </p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        <Sekme
          etkin={sekme === 'konular'}
          tikla={() => sekmeSecimiYaz('konular')}
          etiket="Konular"
        />
        <Sekme etkin={sekme === 'ara'} tikla={() => sekmeSecimiYaz('ara')} etiket="Ara" />
        {denetimListesiGorebilir && (
          <Sekme
            etkin={sekme === 'denetim'}
            tikla={() => sekmeSecimiYaz('denetim')}
            etiket="Doğrulama"
          />
        )}
      </div>

      {!yetkilerHazir && sekme === 'denetim' ? (
        <p className="text-sm text-slate-500">Yetkiler yükleniyor…</p>
      ) : sekme === 'konular' ? (
        <Konular rol={secilenRol} />
      ) : sekme === 'ara' ? (
        <Arama rol={secilenRol} />
      ) : (
        <Denetim
          listeGorebilir={denetimListesiGorebilir}
          ozetGorebilir={kilitOzetiGorebilir}
          yetkilerHazir={yetkilerHazir}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dışa aktarım                                                        */
/* ------------------------------------------------------------------ */

/**
 * İKİ BELGE, TEK KAYNAK.
 *
 * Uç JSON içinde Markdown döndürüyor, dosya değil. `<a href>` ile açılamaz:
 * jetonlu bir uç ve bağlantı `Authorization` başlığı taşımaz — 401 gelirdi.
 * Bu yüzden metin alınıp istemcide `Blob` olarak indiriliyor.
 */
function DisaAktarim({ rol }: { rol?: RehberRol }) {
  const indir = useMutation({
    mutationFn: (tur: DisaAktarimTuru) => rehberDisaAktar(tur, rol),
    onSuccess: (belge) => {
      metinDosyasiIndir(`${dosyaAdiParcasi(belge.baslik)}.md`, belge.icerik);
    },
  });

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex gap-2">
        <Dugme
          tur="ikincil"
          tikla={() => indir.mutate('kilavuz')}
          bekliyor={indir.isPending && indir.variables === 'kilavuz'}
          cocuk="Kılavuzu indir"
        />
        <Dugme
          tur="ikincil"
          tikla={() => indir.mutate('prospektus')}
          bekliyor={indir.isPending && indir.variables === 'prospektus'}
          cocuk="Prospektüsü indir"
        />
      </div>
      <HataKutusu hata={indir.error} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Konular                                                             */
/* ------------------------------------------------------------------ */

function Konular({ rol }: { rol?: RehberRol }) {
  const [konu, konuYaz] = useState<RehberKonu | undefined>(undefined);
  const [tip, tipYaz] = useState<KararTipi | ''>('');
  const [belgeTuru, belgeTuruYaz] = useState<'' | 'kilavuz' | 'prospektus'>('');

  const agac = useQuery({
    queryKey: ['rehber', 'konu-agaci', rol],
    queryFn: () => konuAgaci(rol),
  });

  const bolumler = useQuery({
    queryKey: ['rehber', 'tumu', { rol, konu, tip, belgeTuru }],
    queryFn: () =>
      rehberTumu({
        rol,
        konu,
        tip: tip === '' ? undefined : tip,
        belgeTuru: belgeTuru === '' ? undefined : belgeTuru,
      }),
    // Süzgeç değişince liste boşalıp zıplamasın.
    placeholderData: keepPreviousData,
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
      <aside className="space-y-1">
        <KonuDugmesi
          etkin={konu === undefined}
          tikla={() => konuYaz(undefined)}
          ad="Tüm konular"
          sayi={agac.data?.reduce((toplam, d) => toplam + d.maddeSayisi, 0)}
          bekleyen={agac.data?.reduce((toplam, d) => toplam + d.bekleyenSayisi, 0)}
        />

        {agac.data?.map((dal) => (
          <KonuDugmesi
            key={dal.konu}
            etkin={konu === dal.konu}
            tikla={() => konuYaz(dal.konu)}
            ad={dal.konuAdi}
            sayi={dal.maddeSayisi}
            bekleyen={dal.bekleyenSayisi}
          />
        ))}

        <HataKutusu hata={agac.error} />
      </aside>

      <section className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <label className="text-sm text-slate-700">
            <span className="mr-2">Karar tipi</span>
            <select
              value={tip}
              onChange={(olay) => tipYaz(olay.target.value as KararTipi | '')}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
            >
              <option value="">Tümü</option>
              {(Object.keys(TIP_ETIKETI) as KararTipi[]).map((t) => (
                <option key={t} value={t}>
                  {TIP_ETIKETI[t]}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm text-slate-700">
            <span className="mr-2">Belge</span>
            <select
              value={belgeTuru}
              onChange={(olay) => belgeTuruYaz(olay.target.value as '' | 'kilavuz' | 'prospektus')}
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
            >
              <option value="">Tümü</option>
              <option value="kilavuz">Kılavuz</option>
              <option value="prospektus">Prospektüs</option>
            </select>
          </label>
          {/*
            `ikisi` seçeneği YOK: sunucu süzgeci her iki belgede yer alan
            maddeleri ELEMİYOR, bu yüzden ayrı bir seçenek anlamsız olurdu.
          */}
        </div>

        <HataKutusu hata={bolumler.error} />

        {bolumler.isPending ? (
          <p className="text-sm text-slate-500">Yükleniyor…</p>
        ) : (bolumler.data?.length ?? 0) === 0 ? (
          <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
            Bu süzgeçle madde bulunamadı.
          </p>
        ) : (
          bolumler.data?.map((bolum) => <BolumKutusu key={bolum.bolum} bolum={bolum} />)
        )}
      </section>
    </div>
  );
}

function KonuDugmesi({
  etkin,
  tikla,
  ad,
  sayi,
  bekleyen,
}: {
  etkin: boolean;
  tikla: () => void;
  ad: string;
  sayi?: number;
  bekleyen?: number;
}) {
  return (
    <button
      type="button"
      onClick={tikla}
      aria-current={etkin ? 'true' : undefined}
      className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm ${
        etkin ? 'bg-kurum-50 font-medium text-kurum-800' : 'text-slate-700 hover:bg-slate-50'
      }`}
    >
      <span>{ad}</span>
      <span className="flex items-center gap-1.5">
        {/*
          BEKLEYEN ROZETİ SIFIRDA GİZLENİR. `0` basılsaydı sağlıklı konu ile
          sorunlu konu aynı görsel ağırlığı taşır ve rozet uyarı olmaktan
          çıkardı.
        */}
        {bekleyen !== undefined && bekleyen > 0 && (
          <span
            title={`${bekleyen} madde doğrulama bekliyor`}
            className="rounded-full bg-amber-100 px-1.5 text-xs font-medium text-amber-800"
          >
            {bekleyen}
          </span>
        )}
        {sayi !== undefined && <span className="text-xs text-slate-400">{sayi}</span>}
      </span>
    </button>
  );
}

function BolumKutusu({ bolum }: { bolum: RehberBolumu }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <h2 className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-800">
        {bolum.bolum}
      </h2>

      <ul className="divide-y divide-slate-100">
        {bolum.maddeler.map((madde) => (
          <li key={madde.kod}>
            <Link
              to={`/rehber/madde/${encodeURIComponent(madde.kod)}`}
              className="flex items-start justify-between gap-4 px-4 py-3 hover:bg-slate-50"
            >
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-900">{madde.baslik}</span>
                <span className="mt-0.5 block truncate text-xs text-slate-500">
                  {madde.sistemDavranisi}
                </span>
              </span>

              <span className="flex shrink-0 items-center gap-2">
                {madde.dogrulamaDurumu === 'beklemede' && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                    Doğrulama bekliyor
                  </span>
                )}
                <TipRozeti tip={madde.tip} />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function TipRozeti({ tip }: { tip: KararTipi }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TIP_BICIMI[tip]}`}
    >
      {TIP_ETIKETI[tip]}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Arama                                                               */
/* ------------------------------------------------------------------ */

/**
 * DURUM ARAMASI — "şunu yaşadım".
 *
 * `GET /rehber?arama=` DEĞİL `GET /rehber/ara` kullanılıyor: yalnız bu uç
 * hangi günlük dil ifadesinin eşleştiğini (`eslesme`) döndürüyor ve ucun tek
 * varlık sebebi bu. Kullanıcı "devam kaydı düzeltme" diye aramaz, "yanlış
 * saat girdim" diye arar; sonucun neden geldiğini görmeli.
 */
function Arama({ rol }: { rol?: RehberRol }) {
  const [metin, metinYaz] = useState('');
  const gecikmisMetin = geciktirmeKullan(metin);
  const yeterli = gecikmisMetin.trim().length >= EN_AZ_ARAMA;

  const sonuclar = useQuery({
    queryKey: ['rehber', 'ara', { metin: gecikmisMetin.trim(), rol }],
    queryFn: () => rehberAra(gecikmisMetin.trim(), rol),
    // Tek harfte tüm rehberi taramak anlamsız; iki karakterden önce istek yok.
    enabled: yeterli,
  });

  return (
    <div className="max-w-2xl space-y-4">
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">Ne oldu?</span>
        <input
          type="search"
          value={metin}
          onChange={(olay) => metinYaz(olay.target.value)}
          placeholder="yanlış saat girdim"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
        />
        <span className="mt-1 block text-xs text-slate-500">
          Madde başlığını bilmenize gerek yok; başınıza geleni gündelik dille yazın.
        </span>
      </label>

      <HataKutusu hata={sonuclar.error} />

      {!yeterli ? (
        <p className="text-sm text-slate-500">En az {EN_AZ_ARAMA} karakter yazın.</p>
      ) : sonuclar.isPending ? (
        <p className="text-sm text-slate-500">Aranıyor…</p>
      ) : (sonuclar.data?.length ?? 0) === 0 ? (
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Eşleşen madde yok. Farklı bir ifadeyle deneyin.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
          {sonuclar.data?.map((sonuc) => (
            <li key={sonuc.kod}>
              <Link
                to={`/rehber/madde/${encodeURIComponent(sonuc.kod)}`}
                className="block px-4 py-3 hover:bg-slate-50"
              >
                <span className="block text-sm font-medium text-slate-900">{sonuc.baslik}</span>
                {/*
                  EŞLEŞEN İFADE GÖSTERİLİR. Gösterilmeseydi kullanıcı sonucun
                  neden geldiğini bilemez ve listeye güvenmezdi.
                */}
                {sonuc.eslesme !== null && (
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Eşleşen ifade: “{sonuc.eslesme}”
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Doğrulama (denetim)                                                 */
/* ------------------------------------------------------------------ */

const BEKLEYEN_SUTUNLARI: Sutun<DogrulamaBekleyen>[] = [
  {
    baslik: 'Madde',
    hucre: (s) => (
      <Link
        to={`/rehber/madde/${encodeURIComponent(s.kod)}`}
        className="font-medium text-kurum-700 hover:underline"
      >
        {s.baslik}
      </Link>
    ),
  },
  { baslik: 'Tip', hucre: (s) => <TipRozeti tip={s.tip} />, daralt: true },
  { baslik: 'Parametre', hucre: (s) => s.ilgiliParametre ?? '—', daralt: true },
  { baslik: 'Tercih sebebi', hucre: (s) => s.tercihSebebi ?? '—' },
];

const PARAMETRE_SUTUNLARI: Sutun<DogrulanmamisParametre>[] = [
  { baslik: 'Kod', hucre: (p) => p.kod, daralt: true },
  { baslik: 'Ad', hucre: (p) => p.ad },
  { baslik: 'Değer', hucre: (p) => p.deger, daralt: true },
  { baslik: 'Kaynak', hucre: (p) => p.kaynak, daralt: true },
  {
    baslik: 'Geçerlilik',
    hucre: (p) => tarihMetni(p.gecerlilikBaslangic),
    daralt: true,
    hizala: 'sag',
  },
];

function Denetim({
  listeGorebilir,
  ozetGorebilir,
  yetkilerHazir,
}: {
  listeGorebilir: boolean;
  ozetGorebilir: boolean;
  yetkilerHazir: boolean;
}) {
  const ozet = useQuery({
    queryKey: ['rehber', 'kilit-hazirligi'],
    queryFn: () => kilitHazirligi(),
    // `RAPOR_GORUNTULE` bu uçta KABUL EDİLMİYOR; listelerle aynı koşula
    // bağlansaydı İK kullanıcısı için 403 üretirdi.
    enabled: yetkilerHazir && ozetGorebilir,
  });

  const bekleyenler = useQuery({
    queryKey: ['rehber', 'dogrulama-bekleyenler'],
    queryFn: () => dogrulamaBekleyenler(),
    enabled: yetkilerHazir && listeGorebilir,
  });

  const parametreler = useQuery({
    queryKey: ['rehber', 'dogrulanmamis-parametreler'],
    queryFn: () => dogrulanmamisParametreler(),
    enabled: yetkilerHazir && listeGorebilir,
  });

  return (
    <div className="space-y-6">
      {ozetGorebilir && (
        <>
          <HataKutusu hata={ozet.error} />
          {ozet.data && <KilitOzeti veri={ozet.data} />}
        </>
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-800">Doğrulama bekleyen maddeler</h2>
        <p className="text-xs text-slate-500">
          Hukuk müşavirliği görüşü veya TİS teyidi bekleyenler. Bu liste boşalmadan dönem
          kilitlenemez.
        </p>
        <HataKutusu hata={bekleyenler.error} />
        <Tablo
          sutunlar={BEKLEYEN_SUTUNLARI}
          satirlar={bekleyenler.data ?? []}
          anahtar={(s) => s.kod}
          bekliyor={bekleyenler.isPending}
          bosMetin="Doğrulama bekleyen madde yok."
        />
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-800">Onaysız katsayı ve parametreler</h2>
        <p className="text-xs text-slate-500">
          Dayanağı <code>oneri_varsayilan</code> olanlar: sistem bir değer önerdi, kurum henüz
          onaylamadı. Hesap çalışır ve sonuç üretir — ama sonuç bir ÖNERİDİR.
        </p>
        <HataKutusu hata={parametreler.error} />
        {/*
          SAYFALAMA YOK. Üç ucun hiçbiri sayfalı değil, düz dizi dönüyor.
          `Tablo` ile `Sayfalama`nın ayrı bileşenler olması (SÖZLEŞME §13)
          burada işe yarıyor: birleşik olsalardı anlamsız bir çubuk taşınırdı.
        */}
        <Tablo
          sutunlar={PARAMETRE_SUTUNLARI}
          satirlar={parametreler.data ?? []}
          anahtar={(p) => `${p.kaynak}:${p.kod}`}
          bekliyor={parametreler.isPending}
          bosMetin="Onay bekleyen parametre yok."
        />
      </section>
    </div>
  );
}

/**
 * KİLİT HAZIRLIĞI ÖZETİ.
 *
 * `engeller` KİLİTLEMEYİ DURDURUR, `uyarilar` DURDURMAZ. İkisi ayrı kutuda
 * gösteriliyor; tek listede birleştirilseydi "gerekçe yaz ve devam et" ile
 * "hiç geçilemez" karışırdı.
 */
function KilitOzeti({ veri }: { veri: KilitHazirligi }) {
  return (
    <section
      className={`rounded-lg border p-4 ${
        veri.kilitlenebilirMi ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
      }`}
    >
      <h2 className="text-sm font-semibold text-slate-900">
        {veri.kilitlenebilirMi ? 'Dönem kilitlenebilir' : 'Dönem henüz kilitlenemez'}
      </h2>

      <dl className="mt-3 flex flex-wrap gap-6 text-sm">
        <Sayac etiket="Bekleyen madde" deger={veri.bekleyenMadde} />
        <Sayac etiket="Onaysız parametre" deger={veri.dogrulanmamisParametre} />
        <Sayac
          etiket="Takvim kalan gün"
          deger={veri.takvimKalanGun}
          vurgula={veri.takvimDurumu !== 'normal'}
        />
      </dl>

      {veri.engeller.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-red-700">
            Durduran engeller
          </h3>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-slate-800">
            {veri.engeller.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {veri.uyarilar.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            Uyarılar — durdurmaz
          </h3>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-slate-700">
            {veri.uyarilar.map((u) => (
              <li key={u}>{u}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Sayac({
  etiket,
  deger,
  vurgula = false,
}: {
  etiket: string;
  deger: number;
  vurgula?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs text-slate-600">{etiket}</dt>
      <dd
        className={`text-lg font-semibold ${
          vurgula || deger > 0 ? 'text-amber-800' : 'text-slate-900'
        }`}
      >
        {deger}
      </dd>
    </div>
  );
}

/** Konu ağacı tipini dışa açar — test ve ileride PWA için. */
export type { KonuDali };
